import { useId, useState } from 'react';
import { toUserMessage } from '@platform/api-client';

import {
  BLOCK_LABEL,
  type BlockPath,
  countBlocks,
  emptyBlock,
  insertBlock,
  listAt,
  moveBlock,
  nextMarker,
  removeBlock,
  replaceBlock,
} from '../../lib/pumsemBody';
import { usePumsemImageUpload } from '../../hooks/usePumsemImageUpload';
import { pumsemImageUrl } from '../../lib/pumsemAsset';
import { parseBodyText, splitNotes } from '../../lib/pumsemPaste';
import type {
  PumsemBlock,
  PumsemBlockType,
  PumsemListStyle,
} from '../../model/pumsem.type';
import { PumsemTableEditor } from './PumsemTableEditor';
import { PumsemTablePaste } from './PumsemTablePaste';
import { BTN_GHOST, FIELD, LABEL } from './pumsemFormStyles';

interface PumsemBodyEditorProps {
  body: PumsemBlock[];
  onChange: (body: PumsemBlock[]) => void;
}

/** 원본 4단(`1.` → `가.` → `1)` → `①`)까지. 그 아래는 발행본에도 없다. */
const MAX_DEPTH = 4;

const STYLE_OPTIONS: { value: PumsemListStyle; label: string }[] = [
  { value: 'decimal', label: '1. 2. 3.' },
  { value: 'hangul', label: '가. 나. 다.' },
  { value: 'paren', label: '1) 2) 3)' },
  { value: 'paren-hangul', label: '가) 나) 다)' },
  { value: 'circled', label: '① ② ③' },
];

const ADD_TYPES: PumsemBlockType[] = [
  'paragraph',
  'list',
  'table',
  'figure',
  'formula',
  'notes',
];

/**
 * 본문 편집기 — 문단·번호목록·표·그림·산식을 **한 줄에 세운다.**
 *
 * 예전에는 서술 문단(textarea)과 품 표(표 편집기)가 화면에서 갈라져 있었다.
 * 그러면 "문단 → 표 → 문단" 순서를 만들 수가 없다. 발행본은 그 순서로 나오므로
 * 편집기도 한 줄이어야 한다.
 *
 * 위치는 **경로**로 가리킨다(`lib/pumsemBody.ts`). 중첩 목록 안의 블록을 옮기고
 * 지우는 로직을 화면에 두면 반드시 어긋난다.
 */
export function PumsemBodyEditor({ body, onChange }: PumsemBodyEditorProps) {
  return (
    <div>
      <BlockList blocks={body} path={[]} depth={0} root={body} onChange={onChange} />
      <p className="mt-3 text-[12px] text-neutral-500">
        블록 {countBlocks(body)}개. 발행본의 문단·표·그림 순서를 그대로 세우세요
        — 저장된 순서가 곧 화면 순서입니다.
      </p>
    </div>
  );
}

interface BlockListProps {
  /** 이 경로가 담고 있는 블록들. */
  blocks: PumsemBlock[];
  path: BlockPath;
  depth: number;
  /** 트리 전체. 변경은 항상 뿌리에서 다시 만든다. */
  root: PumsemBlock[];
  onChange: (body: PumsemBlock[]) => void;
}

function BlockList({ blocks, path, depth, root, onChange }: BlockListProps) {
  const [pasting, setPasting] = useState<'table' | 'text' | null>(null);

  return (
    <div className={depth === 0 ? 'space-y-3' : 'mt-2 space-y-2'}>
      {blocks.map((block, index) => (
        <BlockCard
          // 블록에 안정적인 id 가 없다 — 순서가 곧 정체성이다.
          key={`${path.join('.')}-${index}`}
          block={block}
          index={index}
          total={blocks.length}
          path={path}
          depth={depth}
          root={root}
          onChange={onChange}
        />
      ))}

      {pasting === 'table' && (
        <PumsemTablePaste
          onApply={(table) => {
            onChange(insertBlock(root, path, { type: 'table', table }));
            setPasting(null);
          }}
          onCancel={() => setPasting(null)}
        />
      )}

      {pasting === 'text' && (
        <BodyTextPaste
          onApply={(parsed) => {
            onChange(
              parsed.reduce(
                (acc, block) => insertBlock(acc, path, block),
                root,
              ),
            );
            setPasting(null);
          }}
          onCancel={() => setPasting(null)}
        />
      )}

      {pasting === null && (
        <div className="flex flex-wrap gap-1.5">
          {ADD_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              className={BTN_GHOST}
              onClick={() =>
                onChange(insertBlock(root, path, emptyBlock(type)))
              }
            >
              + {BLOCK_LABEL[type]}
            </button>
          ))}
          {/* 982쪽을 한 칸씩 치는 건 현실적이지 않다. 실제 작업은 붙여넣기다. */}
          <button
            type="button"
            className={BTN_GHOST}
            onClick={() => setPasting('text')}
          >
            발행본 본문 붙여넣기
          </button>
          <button
            type="button"
            className={BTN_GHOST}
            onClick={() => setPasting('table')}
          >
            발행본 표 붙여넣기
          </button>
        </div>
      )}
    </div>
  );
}

function BlockCard({
  block,
  index,
  total,
  path,
  depth,
  root,
  onChange,
}: {
  block: PumsemBlock;
  index: number;
  total: number;
  path: BlockPath;
  depth: number;
  root: PumsemBlock[];
  onChange: (body: PumsemBlock[]) => void;
}) {
  const [dropping, setDropping] = useState(false);

  const patch = (next: PumsemBlock) =>
    onChange(replaceBlock(root, path, index, next));

  return (
    <section className="rounded-xl border border-neutral-200 p-3 dark:border-white/12">
      <header className="mb-2 flex items-center gap-2">
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11.5px] font-bold text-neutral-600 dark:bg-white/8 dark:text-neutral-300">
          {BLOCK_LABEL[block.type]}
        </span>
        <span className="ml-auto flex gap-1.5 text-[12px] text-neutral-400">
          <button
            type="button"
            aria-label={`${index + 1}번 블록 위로`}
            disabled={index === 0}
            className="disabled:opacity-30 hover:text-neutral-700"
            onClick={() => onChange(moveBlock(root, path, index, -1))}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`${index + 1}번 블록 아래로`}
            disabled={index === total - 1}
            className="disabled:opacity-30 hover:text-neutral-700"
            onClick={() => onChange(moveBlock(root, path, index, 1))}
          >
            ↓
          </button>
          {/* 표 블록 하나가 20행짜리일 수 있다. 되돌리기가 없으므로 2단계. */}
          {dropping ? (
            <button
              type="button"
              aria-label={`${index + 1}번 블록 정말 삭제`}
              className="font-bold text-danger"
              onClick={() => {
                setDropping(false);
                onChange(removeBlock(root, path, index));
              }}
            >
              정말?
            </button>
          ) : (
            <button
              type="button"
              aria-label={`${index + 1}번 블록 삭제`}
              className="hover:text-danger"
              onClick={() => setDropping(true)}
            >
              ✕
            </button>
          )}
        </span>
      </header>

      <BlockFields
        block={block}
        index={index}
        path={path}
        depth={depth}
        root={root}
        onChange={onChange}
        onPatch={patch}
      />
    </section>
  );
}

function BlockFields({
  block,
  index,
  path,
  depth,
  root,
  onChange,
  onPatch,
}: {
  block: PumsemBlock;
  index: number;
  path: BlockPath;
  depth: number;
  root: PumsemBlock[];
  onChange: (body: PumsemBlock[]) => void;
  onPatch: (block: PumsemBlock) => void;
}) {
  switch (block.type) {
    case 'paragraph':
      return (
        <textarea
          className={`${FIELD} h-20 leading-7`}
          value={block.text}
          placeholder="정부 등 공공기관에서 시행하는 건설공사의 …"
          aria-label="문단 내용"
          onChange={(event) =>
            onPatch({ ...block, text: event.target.value })
          }
        />
      );

    case 'formula':
      return (
        <div className="space-y-2">
          <textarea
            className={`${FIELD} h-16 font-mono leading-7`}
            value={block.text}
            placeholder="Q = 3,600 · q · k · f · E / Cm"
            aria-label="산식"
            onChange={(event) =>
              onPatch({ ...block, text: event.target.value })
            }
          />
          <textarea
            className={`${FIELD} h-16 leading-6`}
            value={block.legend.join('\n')}
            placeholder={'q: 버킷 용량(㎥)\nf: 체적환산계수'}
            aria-label="기호 설명"
            onChange={(event) =>
              onPatch({
                ...block,
                legend: event.target.value
                  .split('\n')
                  .map((line) => line.trim())
                  .filter(Boolean),
              })
            }
          />
        </div>
      );

    case 'figure':
      return <FigureFields block={block} onPatch={onPatch} />;

    case 'notes':
      return <NotesFields block={block} onPatch={onPatch} />;

    case 'table':
      return (
        <PumsemTableEditor
          table={block.table}
          onChange={(table) => onPatch({ ...block, table })}
        />
      );

    case 'list':
      return (
        <div>
          <label className={LABEL} htmlFor={`style-${path.join('.')}-${index}`}>
            번호 모양
          </label>
          <select
            id={`style-${path.join('.')}-${index}`}
            className={`${FIELD} w-40`}
            value={block.style}
            onChange={(event) =>
              onPatch({
                ...block,
                style: event.target.value as PumsemListStyle,
              })
            }
          >
            {STYLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <ul className="mt-2 space-y-2">
            {block.items.map((item, itemIndex) => {
              const patchItem = (next: Partial<typeof item>) =>
                onPatch({
                  ...block,
                  items: block.items.map((current, i) =>
                    i === itemIndex ? { ...current, ...next } : current,
                  ),
                });

              return (
                <li key={itemIndex}>
                  <div className="flex gap-2">
                    {/* 번호는 사람이 적는다 — 개정으로 건너뛴 번호가 있고,
                        번호 자체가 인용 주소다(자동 채번은 원문과 어긋난다). */}
                    <input
                      className={`${FIELD} w-16 text-center`}
                      value={item.marker}
                      aria-label={`${itemIndex + 1}번 항목 번호`}
                      onChange={(event) =>
                        patchItem({ marker: event.target.value })
                      }
                    />
                    <textarea
                      className={`${FIELD} h-10 min-h-10 flex-1 leading-6`}
                      value={item.text}
                      aria-label={`${itemIndex + 1}번 항목 내용`}
                      onChange={(event) =>
                        patchItem({ text: event.target.value })
                      }
                    />
                    <button
                      type="button"
                      aria-label={`${itemIndex + 1}번 항목 삭제`}
                      className="px-1 text-[12px] text-neutral-400 hover:text-danger"
                      onClick={() =>
                        onPatch({
                          ...block,
                          items: block.items.filter((_, i) => i !== itemIndex),
                        })
                      }
                    >
                      ✕
                    </button>
                  </div>

                  {/* 하위 블록. 발행본은 `1.` 안에 `가.` 를, 그 안에 표를 둔다. */}
                  {depth + 1 < MAX_DEPTH && (
                    <div className="mt-1.5 border-l border-neutral-200 pl-3 dark:border-white/12">
                      {item.children.length === 0 ? (
                        <button
                          type="button"
                          className="text-[12px] text-neutral-400 hover:text-neutral-700"
                          onClick={() =>
                            onChange(
                              insertBlock(
                                root,
                                [...path, index, itemIndex],
                                emptyBlock('list'),
                              ),
                            )
                          }
                        >
                          + 하위 목록
                        </button>
                      ) : (
                        <BlockList
                          blocks={listAt(root, [...path, index, itemIndex])}
                          path={[...path, index, itemIndex]}
                          depth={depth + 1}
                          root={root}
                          onChange={onChange}
                        />
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <button
            type="button"
            className={`${BTN_GHOST} mt-2`}
            onClick={() =>
              onPatch({
                ...block,
                items: [
                  ...block.items,
                  { marker: nextMarker(block.items), text: '', children: [] },
                ],
              })
            }
          >
            항목 추가
          </button>
        </div>
      );
  }
}

/**
 * [주] 블록 — 원문에서 [주]가 찍힌 자리(대개 표 바로 밑)에 둔다.
 *
 * 입력 중에는 줄을 그대로 두고, 칸을 벗어날 때 번호 단위로 다시 나눈다 —
 * 칠 때마다 나누면 줄바꿈이 먹혀서 다음 줄로 넘어갈 수가 없다.
 */
function NotesFields({
  block,
  onPatch,
}: {
  block: Extract<PumsemBlock, { type: 'notes' }>;
  onPatch: (block: PumsemBlock) => void;
}) {
  return (
    <div>
      <textarea
        className={`${FIELD} h-24 leading-7`}
        value={block.notes.join('\n')}
        placeholder={'① 본 품은 …\n② 절단, 배관 …'}
        aria-label="[주] 내용"
        onChange={(event) =>
          onPatch({ ...block, notes: event.target.value.split('\n') })
        }
        onBlur={(event) =>
          onPatch({ ...block, notes: splitNotes(event.target.value) })
        }
      />
      <p className="mt-1 text-[12px] text-neutral-500">
        원문에서 이 [주]가 찍힌 자리로 옮기세요(↑↓). <code>[주] ① … ② …</code> 를
        통째로 붙여넣으면 번호 단위로 나눕니다.
      </p>
    </div>
  );
}

/**
 * 도해 블록.
 *
 * 주소를 손으로 치는 칸을 **남겨 둔다** — CI 가 CDN 의 `pumsem/` 아래로 미리
 * 올려둔 그림은 상대 키로 참조하는 게 맞고, 업로드는 그때그때 찍은 도해용이다.
 * 두 출처를 `pumsemImageUrl` 이 절대 URL / 상대 키로 갈라 처리한다.
 */
function FigureFields({
  block,
  onPatch,
}: {
  block: Extract<PumsemBlock, { type: 'figure' }>;
  onPatch: (block: PumsemBlock) => void;
}) {
  const { upload, isUploading, progress, error } = usePumsemImageUpload();
  const inputId = useId();

  const pick = async (file: File | undefined) => {
    if (!file) return;
    const { media } = await upload({ file });
    // 업로드 결과는 절대 CDN URL 이다. alt 가 비어 있으면 파일명으로 채운다 —
    // 빈 alt 는 스크린리더에서 그림이 통째로 사라진다.
    if (media?.url) {
      onPatch({
        ...block,
        src: media.url,
        alt: block.alt || file.name.replace(/\.[^.]+$/, ''),
      });
    }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input
          className={FIELD}
          value={block.src}
          placeholder="CDN 키 또는 절대 URL"
          aria-label="그림 주소"
          onChange={(event) => onPatch({ ...block, src: event.target.value })}
        />
        <input
          className={FIELD}
          value={block.alt}
          placeholder="대체 텍스트"
          aria-label="그림 대체 텍스트"
          onChange={(event) => onPatch({ ...block, alt: event.target.value })}
        />
        <input
          className={FIELD}
          value={block.caption ?? ''}
          placeholder="캡션 (선택)"
          aria-label="그림 캡션"
          onChange={(event) =>
            onPatch({ ...block, caption: event.target.value || null })
          }
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* 실제 컨트롤은 `sr-only` 인 file input 이다. Tailwind 의 `sr-only` 는
            `display:none` 이 아니라 clip 이라 **포커스는 받는데 포커스 링이
            어디에도 안 보인다** — 키보드 사용자는 탭이 사라진 자리에서 Space 를
            눌러야 한다는 걸 알 수 없다. label 에 focus-within 을 건다. */}
        <label
          className={`${BTN_GHOST} cursor-pointer focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-1`}
          htmlFor={inputId}
        >
          {isUploading ? `올리는 중… ${progress}%` : '이미지 올리기'}
        </label>
        <input
          id={inputId}
          type="file"
          className="sr-only"
          accept="image/png,image/jpeg,image/webp"
          disabled={isUploading}
          onChange={(event) => {
            void pick(event.target.files?.[0]);
            // 같은 파일을 다시 고를 수 있어야 한다 — 값을 비우지 않으면
            // 두 번째 선택에서 change 가 안 뜬다.
            event.target.value = '';
          }}
        />
        {block.src && (
          <img
            src={pumsemImageUrl(block.src, { width: 96 })}
            alt=""
            className="h-12 w-auto rounded border border-neutral-200 dark:border-white/12"
          />
        )}
      </div>

      {error && (
        <p className="text-[12.5px] text-danger" role="alert">
          {toUserMessage(error, '이미지 업로드에 실패했습니다.')}
        </p>
      )}
    </div>
  );
}

/**
 * 발행본 본문을 통째로 붙여 블록으로 만든다.
 *
 * 미리보기를 먼저 보여준다 — 파서가 완벽할 수 없고(중첩은 번호 종류로 읽는다),
 * 잘못 읽은 걸 모른 채 적용하면 나중에 화면에서야 드러난다.
 */
function BodyTextPaste({
  onApply,
  onCancel,
}: {
  onApply: (blocks: PumsemBlock[]) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const parsed = text.trim() ? parseBodyText(text) : [];

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-4 dark:border-brand-500/30 dark:bg-brand-500/5">
      <p className="mb-2 text-[13px] font-bold text-neutral-800 dark:text-neutral-100">
        발행본 본문 붙여넣기
      </p>
      <p className="mb-3 text-[12.5px] leading-6 text-neutral-600 dark:text-neutral-400">
        <code>1.</code> <code>가.</code> <code>1)</code> <code>①</code> 번호를
        읽어 목록과 중첩을 만듭니다. 번호 없는 줄은 문단이고, 목록 뒤에 오면
        직전 항목에 이어 붙습니다. <b>번호는 원문 그대로</b> 담습니다.
      </p>

      <textarea
        className={`${FIELD} h-40 font-mono text-[12.5px] leading-6`}
        value={text}
        aria-label="본문 붙여넣기"
        onChange={(event) => setText(event.target.value)}
        placeholder={
          '1. 공사의 예정가격 산정은 본 표준품셈을 활용한다.\n2. 일일 작업시간 8시간을 기준한 것이다.\n가. 콘크리트 구조물 중의 말뚝머리'
        }
      />

      {parsed.length > 0 && (
        <p className="mt-2 text-[12px] text-neutral-500">
          블록 {countBlocks(parsed)}개로 읽었습니다. 적용한 뒤에도 아래에서 그대로
          고칠 수 있습니다.
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          className={BTN_GHOST}
          disabled={parsed.length === 0}
          onClick={() => onApply(parsed)}
        >
          이 내용으로 블록 만들기
        </button>
        <button type="button" className={BTN_GHOST} onClick={onCancel}>
          취소
        </button>
      </div>
    </div>
  );
}
